import { CustomWindow } from '@/types/growthControl/general';
import * as Sentry from '@sentry/nextjs';

const ANONYMOUS_ID_COOKIE = 'bg_anonymousId';
const ANONYMOUS_ID_MAX_AGE_SECONDS = 25920000; // ~300 days (same as GC script)

interface IGrowthControl {
  track: (event: string, payload: Record<string, unknown>) => Promise<void>;
  identify: (event: string, payload: Record<string, unknown>) => Promise<void>;
  reset: () => Promise<void>;
}

export const GrowthControl: IGrowthControl = {
  track: async (event, payload) => {
    return growthControlTrackWrapper('track', event, payload);
  },
  identify: async (event, payload) => {
    return growthControlTrackWrapper('identify', event, payload);
  },
  reset: async () => {
    return growthControlTrackWrapper('reset');
  },
};

function generateUUID(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function readAnonymousIdCookie(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${ANONYMOUS_ID_COOKIE}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeAnonymousIdCookie(id: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${ANONYMOUS_ID_COOKIE}=${encodeURIComponent(
    id
  )}; path=/; max-age=${ANONYMOUS_ID_MAX_AGE_SECONDS}; SameSite=Lax`;
}

/**
 * OneControl returns 500 when identify/track is sent with anonymousId: null.
 * Ensure we always have a string id in cookie + Analytics storage before sending.
 */
function ensureAnonymousId(analytics: CustomWindow['Analytics']): string {
  const existingId = readAnonymousIdCookie();
  if (existingId) {
    analytics.setAnonymousId?.(existingId);
    return existingId;
  }

  const newId = generateUUID();
  analytics.setAnonymousId?.(newId);
  writeAnonymousIdCookie(newId);
  return newId;
}

async function growthControlTrackWrapper(
  method: 'track' | 'identify' | 'reset',
  event?: string,
  payload?: Record<string, unknown>
): Promise<void> {
  if (typeof window === 'undefined') {
    Sentry.captureException(new Error('window is undefined'));
    console.error('window is undefined - ', method);
    return;
  }
  const globalVars = window as unknown as CustomWindow;
  if (!globalVars.Analytics) {
    Sentry.captureException(new Error('Analytics is not defined'));
    console.error('Analytics is not defined - ', method);
    return;
  }

  try {
    if (method === 'reset') {
      return globalVars.Analytics.reset();
    }

    ensureAnonymousId(globalVars.Analytics);
    return globalVars.Analytics[method](event, payload);
  } catch (error) {
    console.error(`Error ${method} event`, error);
    Sentry.setContext('GrowthControl', {
      event,
      payload,
      method,
    });
    Sentry.captureException(error);
  }
}
